import type { ReactNode } from 'react'
import { cn } from '@/utils/cn'
import { AnimatedKpiValue, type KpiValueFormat } from '@/components/ui/AnimatedKpiValue'
import { formatCurrencyCompact } from '@/utils/format-currency'

export type OperacaoMetricAccent = {
  bg: string
  text: string
  ring: string
}

type OperacaoMetricCardProps = {
  label: string
  value: string
  numericValue?: number
  valueFormat?: KpiValueFormat
  subtitle?: string
  trendDelta?: number | null
  trendLabel?: string
  trendPositive?: boolean
  icon: ReactNode
  accent: OperacaoMetricAccent
  valuesVisible?: boolean
  animationDelay?: number
  variant?: 'default' | 'compact'
}

export function OperacaoMetricCard({
  label,
  value,
  numericValue,
  valueFormat = 'text',
  subtitle,
  trendDelta,
  trendLabel = 'vs início do período',
  trendPositive,
  icon,
  accent,
  valuesVisible = true,
  animationDelay = 0,
  variant = 'default',
}: OperacaoMetricCardProps) {
  const isCompact = variant === 'compact'
  const useCompactCurrency =
    valuesVisible && valueFormat === 'currency' && (numericValue ?? 0) >= 100_000
  const displayValue = valuesVisible ? value : '••••'
  const positive = trendPositive ?? (trendDelta == null ? true : trendDelta >= 0)
  const showTrend = valuesVisible && trendDelta != null && Number.isFinite(trendDelta)

  return (
    <article
      className={cn(
        'relative flex h-full flex-col overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] shadow-sm',
        'transition-shadow hover:shadow-md',
        isCompact ? 'p-4' : 'p-5',
      )}
    >
      <div className='flex items-start justify-between gap-3'>
        <p className={cn('font-medium text-text-muted', isCompact ? 'text-xs' : 'text-sm')}>
          {label}
        </p>
        <div
          className={cn(
            'flex shrink-0 items-center justify-center rounded-xl',
            isCompact ? 'h-8 w-8' : 'h-10 w-10',
          )}
          style={{ backgroundColor: accent.bg, color: accent.text }}
        >
          {icon}
        </div>
      </div>

      <div className={cn(isCompact ? 'mt-2' : 'mt-3')}>
        <AnimatedKpiValue
          numericValue={valuesVisible ? numericValue : undefined}
          displayValue={
            useCompactCurrency && numericValue != null
              ? formatCurrencyCompact(numericValue)
              : displayValue
          }
          format={valueFormat}
          delay={animationDelay}
          compactCurrency={useCompactCurrency}
          className={cn(
            'font-bold tracking-tight text-text-primary',
            isCompact ? 'text-2xl' : 'text-3xl',
          )}
        />
      </div>

      <div className={cn('mt-auto flex flex-wrap items-center gap-2', isCompact ? 'pt-2' : 'pt-3')}>
        {showTrend ? (
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
              positive
                ? 'bg-emerald-100 text-emerald-700 [html[data-theme=dark]_&]:bg-emerald-950/40 [html[data-theme=dark]_&]:text-emerald-300'
                : 'bg-red-100 text-red-700 [html[data-theme=dark]_&]:bg-red-950/40 [html[data-theme=dark]_&]:text-red-300',
            )}
          >
            {positive ? '↑' : '↓'} {Math.abs(trendDelta).toFixed(1)}% {trendLabel}
          </span>
        ) : null}
        {subtitle ? (
          <span className='text-xs text-text-muted'>{subtitle}</span>
        ) : null}
      </div>
    </article>
  )
}

export const OPERACAO_METRIC_ACCENTS = {
  pipeline: { bg: '#ede9fe', text: '#7c3aed', ring: '#c4b5fd' },
  closed: { bg: '#dcfce7', text: '#16a34a', ring: '#86efac' },
  winRate: { bg: '#fef3c7', text: '#d97706', ring: '#fcd34d' },
  active: { bg: '#dbeafe', text: '#2563eb', ring: '#93c5fd' },
} as const satisfies Record<string, OperacaoMetricAccent>
